import type { InvoiceAmount, PlanChangePreview, PlanChangeResult } from '@/api/types'
import { centsLabel, planRank } from '@/lib/billing'

/** Pure copy for the change-plan dialog: the proration preview and the outcome sentences (BI7). */

export interface PreviewSummary {
  /** `upgrade` applies today and charges the prorated amount; `downgrade` waits for the period end. */
  immediate: boolean
  headline: string
  dueNow: string | null
  detail: string
}

export function planName(code: string): string {
  return code === 'team' ? 'Team' : code === 'solo' ? 'Solo' : code === 'free' ? 'Free' : code
}

export function intervalName(interval: 'month' | 'year'): string {
  return interval === 'month' ? 'monthly' : 'annual'
}

export function isUpgrade(
  current: { plan_code: string; interval: 'month' | 'year' | null },
  next: { plan: string; interval: 'month' | 'year' },
): boolean {
  if (planRank(next.plan) !== planRank(current.plan_code)) return planRank(next.plan) > planRank(current.plan_code)
  return next.interval === 'year' && current.interval === 'month'
}

function money(amount: InvoiceAmount | null | undefined): string | null {
  return amount === null || amount === undefined ? null : centsLabel(amount.total_cents)
}

export function summarisePreview(preview: PlanChangePreview, formatDate: (iso: string | null) => string): PreviewSummary {
  const target = `${planName(preview.plan_code)} ${intervalName(preview.interval)}`
  if (preview.direction === 'keep') {
    // bug-223: back to the plan in force — the scheduled change is released, nothing is charged.
    return {
      immediate: false,
      headline: `Keep ${target}`,
      dueNow: null,
      detail: `The scheduled plan change is cancelled. Nothing is charged today, and your plan renews as ${target}, as it does now.`,
    }
  }
  const immediate = preview.direction === 'upgrade'
  const renewal = money(preview.next_renewal)
  const renewalText =
    renewal === null ? '' : ` From then on you are billed ${renewal} per ${preview.interval === 'month' ? 'month' : 'year'}, plus tax where it applies.`
  if (immediate) {
    const due = money(preview.due_now)
    return {
      immediate,
      headline: `Upgrade to ${target} now`,
      dueNow: due,
      detail: `You are charged ${due ?? 'the prorated difference'} today on your default card, for the rest of this period.${renewalText}`,
    }
  }
  return {
    immediate,
    headline: `Change to ${target} at the end of the period`,
    dueNow: null,
    detail: `Nothing is charged today. You keep your current plan until ${formatDate(preview.effective_at)}, then the change takes effect.${renewalText} You can undo it until then.`,
  }
}

export function outcomeNotice(result: PlanChangeResult, formatDate: (iso: string | null) => string): string {
  const target = `${planName(result.plan_code)} ${intervalName(result.interval)}`
  switch (result.outcome) {
    case 'applied':
      return `Plan changed to ${target}.`
    case 'scheduled':
      return `Scheduled: ${target} starts ${formatDate(result.effective_at)}.`
    case 'kept':
      return `You keep ${target}. The scheduled change is cancelled and nothing was charged.`
    default:
      return `The card was declined. Pay the open invoice with another card to finish the change to ${target}.`
  }
}

const PLAN_ERRORS: Record<string, string> = {
  no_subscription: 'There is no live subscription to change.',
  no_change: 'That is already your plan.',
  no_scheduled_change: 'There is no scheduled change to undo.',
  subscription_canceling: 'The subscription is set to cancel. Resume it before changing the plan.',
  plan_change_refused: 'Stripe refused the change.',
}

export function planErrorText(code: string | undefined, message: string): string {
  return (code !== undefined ? PLAN_ERRORS[code] : undefined) ?? message
}

/**
 * The plan card's action while a subscription is live (bug-223). The plan in force offers nothing —
 * unless a change is scheduled, when choosing it again is how to keep it (`keep`, free).
 */
export function liveCardAction(
  current: { plan_code: string; interval: 'month' | 'year' | null },
  card: { plan: string; interval: 'month' | 'year' },
  scheduled: { plan_code: string; interval: 'month' | 'year' } | null,
): 'none' | 'keep' | 'upgrade' | 'change' {
  const inForce = current.plan_code === card.plan && current.interval === card.interval
  if (inForce) return scheduled === null ? 'none' : 'keep'
  if (scheduled !== null && scheduled.plan_code === card.plan && scheduled.interval === card.interval) return 'none'
  return isUpgrade(current, card) ? 'upgrade' : 'change'
}

/**
 * bug-224: subscribing with a default card on file is charged at once, server-side, with no card form
 * — so it is confirmed first, and a complimentary plan is said to end.
 */
export function subscribeConfirmText(input: {
  plan: string
  interval: 'month' | 'year'
  price: string | null
  card: string
  complimentaryPlan: string | null
}): string {
  const target = `${planName(input.plan)} ${intervalName(input.interval)}`
  const price = input.price === null ? '' : ` (${input.price}, plus tax where it applies)`
  const comp =
    input.complimentaryPlan === null
      ? ''
      : ` Your complimentary ${planName(input.complimentaryPlan)} ends when the paid plan starts, today.`
  return `${target}${price} is charged today to ${input.card}, then renews automatically.${comp}`
}
